'use client';

import { Check } from 'lucide-react';
import { useMemo } from 'react';
import { ItemIcon, MenuGroup, MenuItem } from '../primitives';
import type { CommandPickerItem } from '../types';

export function StaticPicker({
  term,
  heading,
  items,
  emptyLabel,
  onDone,
}: {
  term: string;
  heading: string;
  items: CommandPickerItem[];
  emptyLabel: string;
  onDone: () => void;
}) {
  const filtered = useMemo(() => {
    const lower = term.trim().toLowerCase();
    if (!lower) return items;
    return items.filter((item) =>
      [item.label, item.hint, ...(item.keywords ?? [])].some((text) =>
        text?.toLowerCase().includes(lower)
      )
    );
  }, [items, term]);

  if (filtered.length === 0) {
    return (
      <div className="text-muted-foreground/60 px-3 py-4 text-center text-sm">
        {emptyLabel}
      </div>
    );
  }

  return (
    <MenuGroup heading={heading}>
      {filtered.map((item) => {
        const Icon = item.icon;
        return (
          <MenuItem
            key={item.id}
            value={`picker:${item.id}`}
            onSelect={() => {
              void Promise.resolve(item.onSelect())
                .catch((error) => {
                  console.error('Command picker item selection failed:', error);
                })
                .finally(onDone);
            }}
          >
            {Icon && <ItemIcon icon={Icon} />}
            <span lang={item.lang} className="min-w-0 flex-1 truncate">
              {item.label}
            </span>
            {item.hint && (
              <span className="text-muted-foreground shrink-0 text-xs">
                {item.hint}
              </span>
            )}
            {item.selected && (
              <Check className="text-primary size-3.5 shrink-0" />
            )}
          </MenuItem>
        );
      })}
    </MenuGroup>
  );
}
